/**
 * "Held-out tests" (TRA-1434): staff pick the benchmark tests a team's
 * training dataset validates on and never trains on. Until a team has
 * them, its "Training dataset" job cannot run.
 *
 * The api offers the tests of the latest scoring night by their path in
 * the repository, which is what gets saved, so nobody types a path. A save
 * replaces the whole list and carries the revision this dialog read; when
 * the saved tests changed in the meantime the api refuses it, the hook
 * reloads them, and the form below starts again from what is saved now.
 *
 * Above the tests sits where the next scheduled run starts today (TRA-1662).
 * A save does not move it: it moves after a build (PlannedStart.tsx).
 *
 * Mounted by the Training page's data menu only while it is open.
 */

import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useHeldOutTests, useSaveHeldOutTests, useTrainingSchedule } from '@/hooks/useTrainingRuns'

import { HeldOutTestsForm } from './HeldOutTestsForm'
import { TodaysPlan } from './PlannedStart'

export function HeldOutTestsDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const heldOut = useHeldOutTests()
  const schedule = useTrainingSchedule()
  const save = useSaveHeldOutTests()
  const data = heldOut.data

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Escape, the overlay and the corner X all land here; none of them
        // may dismiss a save that is still running.
        if (!next && !save.isPending) onClose()
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Held-out tests</DialogTitle>
          <DialogDescription>
            Held-out tests never train the model. The training dataset validates on them.
          </DialogDescription>
        </DialogHeader>
        <TodaysPlan data={schedule.data} isError={schedule.isError} />
        {heldOut.isError ? (
          <p className="text-sm text-destructive">
            {getApiErrorDetail(heldOut.error) ?? 'The held-out tests did not load.'}
          </p>
        ) : data === undefined ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <HeldOutTestsForm
            key={data.revision ?? 'none'}
            data={data}
            isPending={save.isPending}
            onCancel={onClose}
            onSave={(sourceModules) => {
              save.mutate(
                { data: { source_modules: sourceModules, expected_revision: data.revision } },
                {
                  onSuccess: () => {
                    toast.success('Held-out tests saved')
                    onClose()
                  },
                },
              )
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
